<template>
  <div
    class="bui-tbl-col-header"
    :class="[`kind-${kind}`, { sortable, sorted: !!sortDirection, first: isFirst, last: isLast, narrow }]"
    role="columnheader"
    :aria-sort="ariaSort"
  >
    <component
      :is="sortable ? 'button' : 'div'"
      v-bind="sortable ? { type: 'button' } : {}"
      class="trigger"
      :class="{ alone: kind === 'actions' || kind === 'actionsList' || kind === 'usedBy' }"
      @click="onSortClick"
    >
      <span v-if="kind === 'checkbox'" class="chk-slot">
        <slot name="control" />
      </span>
      <span v-if="label" class="label">{{ label }}</span>
      <span v-if="sortable" class="caret">
        <TblSortCaret :sort="sortDirection || 'unsorted'" />
      </span>
    </component>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import TblSortCaret from '../../atoms/TblSortCaret/TblSortCaret.vue';

/**
 * Header cell variants follow Figma `bui.atm.tbl.colHeader` (620:7163):
 *   name, namePreview (checkbox + name), preview (checkbox + "PREVIEW" label),
 *   text, actions, actionsList, checkbox.
 */
const props = withDefaults(
  defineProps<{
    label?: string;
    kind?: 'name' | 'namePreview' | 'preview' | 'text' | 'actions' | 'actionsList' | 'usedBy' | 'checkbox';
    sortable?: boolean;
    sortDirection?: 'asc' | 'desc' | null;
    isFirst?: boolean;
    isLast?: boolean;
    narrow?: boolean;
  }>(),
  {
    kind: 'text',
    sortable: false,
    sortDirection: null,
    isFirst: false,
    isLast: false,
    narrow: false,
  },
);

const emit = defineEmits<{ sortClick: [] }>();

const ariaSort = computed<'ascending' | 'descending' | 'none' | undefined>(() => {
  if (!props.sortable) return undefined;
  if (props.sortDirection === 'asc') return 'ascending';
  if (props.sortDirection === 'desc') return 'descending';
  return 'none';
});

function onSortClick() {
  if (props.sortable) emit('sortClick');
}
</script>

<style lang="scss" scoped>
.bui-tbl-col-header {
  display: flex;
  align-items: center;
  height: var(--bui-tbl-head-h, 52px);
  padding: 0 var(--bui-tbl-cell-pad-x, 16px);
  background: var(--bui-tbl-head-fill, #a420c8);
  color: var(--bui-tbl-head-text, #fffcfd);
  box-sizing: border-box;
  overflow: hidden;
  flex-shrink: 0;
  position: relative;

  // Inner divider lines between header cells (skip only the table's leading edge).
  &:not(.first) {
    border-left: 1px solid var(--bui-tbl-head-divider, rgba(255, 252, 253, 0.08));
  }

  &.kind-actions,
  &.kind-actionsList,
  &.kind-usedBy {
    justify-content: center;
    padding: 0 8px;
  }
}

.trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: 0;
  padding: 0;
  margin: 0;
  color: inherit;
  font-family: inherit;
  cursor: default;

  .bui-tbl-col-header.sortable & {
    cursor: pointer;
  }

  .label {
    display: inline-flex;
    min-height: 14px;
    align-items: center;
    font-size: var(--bui-tbl-head-font-size, 12px);
    line-height: var(--bui-tbl-head-line-height, 14.4px);
    letter-spacing: var(--bui-tbl-head-letter-spacing, 0.48px);
    text-transform: uppercase;
    text-align: left;
    white-space: nowrap;
  }

  .caret {
    display: inline-flex;
    align-items: center;
    line-height: 0;
  }

  .chk-slot {
    display: inline-flex;
    align-items: center;
    margin-right: 8px;
  }
}

.trigger:focus-visible {
  outline: 2px solid var(--bui-page-head, #fffcfd);
  outline-offset: 2px;
  border-radius: 2px;
}
</style>
